import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIsMobileViewport } from '../utils/useIsMobileViewport';
import './PageBar.css';

/**
 * Generic, reusable "page bar": the full-width sticky strip that sits
 * directly under the navbar on top-level pages (Families list, Member
 * list, individual Member detail, ...).
 *
 * Visually identical to the old MemberBar — left side carries a
 * breadcrumb trail (with optional leading chip), right side carries a
 * single primary action and an optional kebab overflow menu. The
 * member-page-specific variant is a thin wrapper that composes this
 * component with member-flavoured crumbs / chip / actions.
 *
 * Behaviour:
 *   - `position: sticky` at `top: var(--navbar-height)`; height 64 px
 *     on desktop, 52 px on mobile (CSS-driven).
 *   - Toggles a `.is-scrolled` class once `window.scrollY > 8` so the
 *     consumer can layer a subtle shadow/border on scroll.
 *   - Kebab opens on click, closes on outside-click or Escape, and
 *     traps the open state with a click-outside listener that's only
 *     mounted while open (cheap and keeps a11y predictable).
 *   - Mobile collapses intermediate breadcrumbs to just the leaf
 *     (handled in PageBar.css under @media (max-width: 768px)).
 *   - All interactive elements stay above the 44 px tap-target line
 *     via min-width / height rules in CSS.
 */

export interface PageBarCrumbLink {
  label: string;
  to: string;
  /** Optional element rendered before the label (e.g. a small icon). */
  icon?: React.ReactNode;
}

export interface PageBarCrumbLeaf {
  label: string;
}

export type PageBarCrumb = PageBarCrumbLink | PageBarCrumbLeaf;

export interface PageBarMenuItem {
  /** Renders as a `<button>` unless `to` is provided. */
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  to?: string;
  danger?: boolean;
  disabled?: boolean;
  /** Optional override for the rendered label while busy (e.g. "Se șterge…"). */
  busy?: boolean;
  busyLabel?: string;
}

export interface PageBarPrimaryAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Custom aria-label; defaults to `label`. */
  ariaLabel?: string;
}

export interface PageBarIconAction {
  /** Visual icon (SVG component). Rendered inside a 36×36 button. */
  icon: React.ReactNode;
  /** Visible tooltip + accessible `aria-label`. */
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** When true, the button is disabled and shows a subtle pulse; the
   *  optional `busyLabel` replaces the tooltip while busy. */
  busy?: boolean;
  busyLabel?: string;
  /** `'danger'` flips the icon tone to red on hover (reserved; default
   *  is the same slate-700 outline used by the existing icon-only
   *  buttons in the bar). */
  variant?: 'default' | 'danger';
}

export interface PageBarProps {
  /** Ordered crumbs, root first. Items with `to` render as links; the
   *  last item is treated as the leaf regardless (it's bolded, not
   *  clickable, even if `to` is set). */
  crumbs: PageBarCrumb[];
  /** Optional decorative chip rendered at the very start of the
   *  breadcrumb row — e.g. member initials, a folder icon, etc. */
  leadingChip?: React.ReactNode;
  /** Right-aligned call-to-action; renders inline with the kebab. */
  primaryAction?: PageBarPrimaryAction;
  /** Inline icon-only buttons rendered between `primaryAction` and the
   *  kebab. Use for the 2-3 most prominent verbs (Edit, Share, …) so
   *  the user doesn't have to drill into the overflow menu for the
   *  common ones. Anything beyond that should stay in `menuItems`. */
  iconActions?: PageBarIconAction[];
  /** Right-aligned overflow menu items. The kebab button is hidden
   *  entirely when this array is empty/undefined. */
  menuItems?: PageBarMenuItem[];
  /** Accessible label for the wrapping `<nav>`. Defaults to
   *  "Navigare pagină". */
  ariaLabel?: string;
  /** Route to navigate to when the mobile back button is tapped. When
   *  omitted, the target is auto-derived: walk `crumbs` backwards from
   *  the second-to-last entry and pick the first one with a `to`
   *  field — that's the conceptual parent. If nothing has a `to`,
   *  no back button is rendered.
   *
   *  Uses `navigate(backTo)` (push), NOT `navigate(-1)` — the user
   *  wants "up one level in the tree", not "browser back" (which can
   *  land on an unrelated page from a previous tab session). */
  backTo?: string;
}

const IconKebab: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="5" r="1.4" />
    <circle cx="12" cy="12" r="1.4" />
    <circle cx="12" cy="19" r="1.4" />
  </svg>
);

const IconBack: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

function isLinkCrumb(c: PageBarCrumb): c is PageBarCrumbLink {
  return typeof (c as PageBarCrumbLink).to === 'string';
}

const PageBar: React.FC<PageBarProps> = ({
  crumbs,
  leadingChip,
  primaryAction,
  iconActions,
  menuItems,
  ariaLabel = 'Navigare pagină',
  backTo,
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobileViewport();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Resolve the "up one level" target: explicit `backTo` wins, else
  // walk `crumbs` from the second-to-last entry backwards and pick
  // the first link crumb encountered. If nothing has a `to`, no back
  // button is rendered (caller-safe — explicit > implicit > nothing).
  const resolvedBackTo = useMemo<string | null>(() => {
    if (typeof backTo === 'string' && backTo.length > 0) return backTo;
    for (let i = crumbs.length - 2; i >= 0; i--) {
      const c = crumbs[i];
      if (isLinkCrumb(c)) return c.to;
    }
    return null;
  }, [backTo, crumbs]);

  const showBackBtn = isMobile && resolvedBackTo != null;

  // Sticky-bar shadow on scroll. Cheap rAF-throttled passive listener.
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 8);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close kebab menu on outside click / Escape. Listeners only mounted
  // while open so we don't pay for them in the common closed state.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (ev: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(ev.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const hasMenu = !!menuItems && menuItems.length > 0;
  const hasIconActions = !!iconActions && iconActions.length > 0;
  const lastIdx = crumbs.length - 1;

  return (
    <div
      className={`page-bar${scrolled ? ' is-scrolled' : ''}${showBackBtn ? ' has-back-btn' : ''}`}
      role="navigation"
      aria-label={ariaLabel}
    >
      <div className="page-bar-inner">
        {showBackBtn && resolvedBackTo ? (
          <button
            type="button"
            className="page-bar-back-btn"
            onClick={() => navigate(resolvedBackTo)}
            aria-label="Înapoi"
            title="Înapoi"
          >
            <IconBack />
          </button>
        ) : null}

        <div className="page-bar-crumb">
          {leadingChip}

          {crumbs.map((c, idx) => {
            const isLeaf = idx === lastIdx;
            const sep =
              idx > 0 ? (
                <span key={`sep-${idx}`} className="page-bar-crumb-sep" aria-hidden>
                  ›
                </span>
              ) : null;

            if (isLeaf || !isLinkCrumb(c)) {
              return (
                <React.Fragment key={`leaf-${idx}`}>
                  {sep}
                  <span
                    className={isLeaf ? 'page-bar-crumb-name' : 'page-bar-crumb-static'}
                    title={c.label}
                    aria-current={isLeaf ? 'page' : undefined}
                  >
                    {c.label}
                  </span>
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key={`link-${idx}`}>
                {sep}
                <Link to={c.to} title={c.label}>
                  {c.icon ? <span className="page-bar-crumb-link-icon" aria-hidden>{c.icon}</span> : null}
                  {c.label}
                </Link>
              </React.Fragment>
            );
          })}
        </div>

        <div className="page-bar-actions">
          {primaryAction ? (
            <button
              type="button"
              className="btn btn-primary page-bar-primary"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              aria-label={primaryAction.ariaLabel ?? primaryAction.label}
            >
              {primaryAction.icon}
              <span className="page-bar-primary-label">{primaryAction.label}</span>
            </button>
          ) : null}

          {hasIconActions ? (
            <div className="page-bar-icon-actions" role="group" aria-label="Acțiuni rapide">
              {iconActions!.map((action, i) => {
                const isBusy = !!action.busy;
                const title = isBusy && action.busyLabel ? action.busyLabel : action.label;
                const cls = `page-bar-icon-action${action.variant === 'danger' ? ' is-danger' : ''}${isBusy ? ' is-busy' : ''}`;
                return (
                  <button
                    key={`ia-${i}`}
                    type="button"
                    className={cls}
                    onClick={action.onClick}
                    disabled={action.disabled || isBusy}
                    aria-label={title}
                    aria-busy={isBusy || undefined}
                    title={title}
                  >
                    <span className="page-bar-icon-action-icon" aria-hidden>
                      {action.icon}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}

          {hasMenu ? (
            <div className="page-bar-menu" ref={menuRef}>
              <button
                type="button"
                className="btn btn-secondary btn-icon-only"
                aria-label="Mai multe acțiuni"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
                title="Mai multe acțiuni"
              >
                <IconKebab />
              </button>
              {menuOpen && (
                <div className="page-bar-menu-panel" role="menu">
                  {menuItems!.map((item, i) => {
                    const cls = `page-bar-menu-item${item.danger ? ' is-danger' : ''}`;
                    const content = (
                      <>
                        {item.icon ? (
                          <span className="page-bar-menu-item-icon" aria-hidden>
                            {item.icon}
                          </span>
                        ) : null}
                        <span>{item.busy && item.busyLabel ? item.busyLabel : item.label}</span>
                      </>
                    );
                    if (item.to) {
                      return (
                        <Link
                          key={`mi-${i}`}
                          to={item.to}
                          className={cls}
                          role="menuitem"
                          onClick={() => {
                            setMenuOpen(false);
                            item.onClick?.();
                          }}
                        >
                          {content}
                        </Link>
                      );
                    }
                    return (
                      <button
                        key={`mi-${i}`}
                        type="button"
                        className={cls}
                        role="menuitem"
                        disabled={item.disabled || item.busy}
                        onClick={() => {
                          setMenuOpen(false);
                          item.onClick?.();
                        }}
                      >
                        {content}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PageBar;
